import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, CheckCircle2, Loader2, ExternalLink, Link2, KeyRound, ChevronDown, Smartphone, Boxes, Rocket, ShieldCheck, Inbox } from "lucide-react";
import { InstagramInbox } from "@/components/InstagramInbox";

const CopyRow = ({ label, value }) => (
  <div className="mt-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">{label}</div>
    <div className="flex items-center gap-2">
      <code className="flex-1 text-[11px] font-mono text-pink-200 bg-[#0b0e14] border border-white/10 rounded px-2 py-1.5 truncate">{value || "—"}</code>
      <button onClick={() => { navigator.clipboard.writeText(value || ""); toast.success("Copied — now paste it into Meta"); }} className="text-[10px] font-mono text-zinc-200 border border-white/10 rounded px-2 py-1.5 hover:text-pink-300 shrink-0">Copy</button>
    </div>
  </div>
);

const Phase = ({ n, icon: Icon, title, subtitle, openPhase, setOpenPhase, done, children }) => {
  const open = openPhase === n;
  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${open ? "border-pink-400/40 bg-pink-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <button data-testid={`ig-phase-${n}`} onClick={() => setOpenPhase(open ? 0 : n)} className="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-white/[0.03]">
        <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${done ? "bg-emerald-500/20 text-emerald-300" : "bg-pink-500/15 text-pink-300"}`}>
          {done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-tactical font-bold text-sm text-zinc-100">Step {n} · {title}</div>
          <div className="text-[11px] text-zinc-500">{subtitle}</div>
        </div>
        <ChevronDown size={16} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3.5 pb-4 pt-1 anim-slidein">{children}</div>}
    </div>
  );
};

const Why = ({ children }) => (
  <div className="text-[11px] text-cyan-200/80 bg-cyan-500/[0.06] border border-cyan-500/20 rounded-lg px-2.5 py-1.5 mb-3">💡 {children}</div>
);

const Steps = ({ items }) => (
  <ol className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2.5 text-[12.5px] text-zinc-300 leading-relaxed">
        <span className="w-5 h-5 rounded-full bg-white/10 text-[10px] font-mono grid place-items-center shrink-0 mt-0.5 text-zinc-300">{i + 1}</span>
        <span>{it}</span>
      </li>
    ))}
  </ol>
);

export const InstagramConnect = () => {
  const [status, setStatus] = useState(null);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);
  const [openPhase, setOpenPhase] = useState(1);
  const [view, setView] = useState("setup");
  const [goLive, setGoLive] = useState(false);

  const load = async () => { try { const s = await api.instagramStatus(); setStatus(s); return s; } catch { return null; } };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("instagram");
    if (p === "connected") { toast.success("Instagram connected! 🎉"); load().then((s) => { if ((s?.connected || []).length) { setOpen(true); setView("inbox"); } }); }
    else if (p === "error") { toast.error("Connection failed — double-check Step 2 & 3 and try again."); }
  }, []);

  const save = async () => {
    if (!appId.trim() || !appSecret.trim()) { toast.error("Paste both the Instagram App ID and Secret"); return; }
    setSaving(true);
    try { await api.saveMetaConfig(appId.trim(), appSecret.trim()); toast.success("Saved! Open Step 3 to finish."); setAppSecret(""); await load(); setOpenPhase(3); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const connect = async () => {
    try { const r = await api.instagramLogin(); window.location.href = r.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Finish Step 2 first (save your keys)"); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.instagramTest();
      if (!r.connected) toast.error(r.reason || "No account connected yet — tap Connect first.");
      else if (r.token_ok && r.subscribed) toast.success(`All good — @${r.username} is connected and receiving DMs.`);
      else if (r.token_ok) toast.warning(`@${r.username} connected, but 'messages' isn't subscribed yet. Re-check the Webhooks step.`);
      else toast.error(r.reason || "Token check failed — reconnect.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not test"); }
    finally { setTesting(false); }
  };

  const s = status || {};
  const connected = (s.connected || []).length > 0;

  return (
    <div data-testid="instagram-connect-card" className={`rounded-xl border p-4 mb-4 ${connected ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-pink-500/30 bg-pink-500/[0.05]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg grid place-items-center bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] shrink-0"><Instagram size={20} className="text-white" /></div>
          <div>
            <div className="font-tactical font-bold text-sm text-zinc-100">Instagram DMs</div>
            {connected ? (
              <div className="text-[11px] font-mono text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> {s.connected.map((a) => `@${a.username || a.ig_id}`).join(", ")}</div>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500">See & send every DM in one place — no Facebook Page needed.</div>
            )}
          </div>
        </div>
        <Button data-testid="instagram-setup-toggle" onClick={() => setOpen((o) => !o)} className="bg-white/10 hover:bg-white/20 text-zinc-100 font-tactical font-bold uppercase text-xs">{open ? "Hide" : (connected ? "Open" : (s.configured ? "Continue" : "Start setup"))}</Button>
      </div>

      {open && (
        <div className="mt-4 border-t border-white/10 pt-4 anim-slidein space-y-2.5" data-testid="instagram-setup-form">
          {connected && (
            <div className="flex items-center gap-2 mb-1">
              <button data-testid="ig-view-inbox" onClick={() => setView("inbox")} className={`text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${view === "inbox" ? "border-pink-400/50 bg-pink-500/15 text-pink-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}><Inbox size={12} /> DM Inbox</button>
              <button data-testid="ig-view-setup" onClick={() => setView("setup")} className={`text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${view === "setup" ? "border-pink-400/50 bg-pink-500/15 text-pink-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}><ShieldCheck size={12} /> Connection</button>
            </div>
          )}

          {connected && view === "inbox" ? (
            <InstagramInbox />
          ) : (
          <>
          <div className="text-[12px] text-zinc-400 mb-1">Three steps. You'll flip one setting on your phone, create a free Meta app once, then paste <strong className="text-pink-300">two codes</strong> here. No Facebook Page, no coding.</div>

          <Phase n={1} icon={Smartphone} title="Make Instagram a Professional account" subtitle="On your phone — 1 minute" openPhase={openPhase} setOpenPhase={setOpenPhase}>
            <Why>Instagram only lets apps read & send DMs for <strong>Professional</strong> (Business or Creator) accounts.</Why>
            <Steps items={[
              "Open the Instagram app and log into the account.",
              "Go to your profile → tap the ☰ menu (top-right) → Settings and privacy.",
              <>Tap <strong>Account type and tools → Switch to professional account</strong> → pick <strong>Business</strong> (or Creator).</>,
              <>In <strong>Settings → Messages and story replies → Message controls</strong>, make sure <strong>Connected tools / Allow access</strong> is ON.</>,
            ]} />
          </Phase>

          <Phase n={2} icon={Boxes} title="Create the app & copy 2 codes" subtitle="On Meta for Developers — do this once" openPhase={openPhase} setOpenPhase={setOpenPhase} done={s.configured}>
            <Why>This creates the <strong>key</strong> that lets LifeOS talk to Instagram. You copy two codes and paste them below — that's the whole job.</Why>
            <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-tactical font-bold text-pink-300 border border-pink-400/30 rounded-lg px-3 py-1.5 mb-3 hover:bg-pink-500/15">Open Meta for Developers <ExternalLink size={12} /></a>
            <Steps items={[
              <>Click <strong>Create app</strong>. If asked for a use case, choose <strong>Other</strong> → app type <strong>Business</strong> → name it <strong>"LifeOS"</strong> → Create.</>,
              <>On the dashboard find <strong>Instagram</strong> → click <strong>Set up</strong>. This adds <strong>“API setup with Instagram login.”</strong> (Do NOT pick the Facebook Login option.)</>,
              <>Open <strong>Instagram → API setup with Instagram login</strong>. Under <strong>“1. Add account”</strong>, add your Instagram account and approve.</>,
              <>Still on that page, find <strong>Instagram app ID</strong> and <strong>Instagram app secret</strong> — copy both into the boxes below and hit Save.</>,
            ]} />
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Instagram App ID</Label>
                <Input data-testid="meta-app-id-input" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm mt-1" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="e.g. 1234567890123456" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Instagram App Secret</Label>
                <Input data-testid="meta-app-secret-input" type="password" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm mt-1" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder="click Show in Meta, then paste" />
              </div>
            </div>
            <Button data-testid="meta-save-config" onClick={save} disabled={saving} className="mt-3 bg-pink-500 hover:bg-pink-400 text-[#04070c] font-tactical font-bold uppercase text-xs">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <KeyRound size={14} className="mr-1" />}{saving ? "Saving" : "Save the 2 codes"}</Button>
            {s.configured && <div className="text-[11px] text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 size={12} /> Saved. Go to Step 3 to finish.</div>}
          </Phase>

          <Phase n={3} icon={Rocket} title="Paste 3 values & turn it on" subtitle={s.configured ? "Paste into Meta, then Connect" : "Finish Step 2 first"} openPhase={openPhase} setOpenPhase={setOpenPhase}>
            {!s.configured ? (
              <p className="text-[12px] text-zinc-500">Save your two codes in Step 2 and this unlocks.</p>
            ) : (
              <>
                <Why>These tell Instagram where to send your DMs and where to send you back after login. Copy each and paste where Meta asks.</Why>
                <div className="text-[12px] text-zinc-300 mb-1">In <strong>Instagram → API setup with Instagram login → “Business login settings”</strong>, paste:</div>
                <CopyRow label="① OAuth Redirect URI (Business login settings)" value={s.callback_url} />
                <div className="text-[12px] text-zinc-300 mt-3 mb-1">Then open <strong>Instagram → Webhooks</strong>, paste these and <strong>subscribe to “messages”</strong>:</div>
                <CopyRow label="② Callback URL (Webhooks)" value={s.webhook_url} />
                <CopyRow label="③ Verify token (Webhooks)" value={s.verify_token} />
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <Button data-testid="instagram-connect-button" onClick={connect} className="bg-pink-500 hover:bg-pink-400 text-[#04070c] font-tactical font-bold uppercase text-xs"><Link2 size={14} className="mr-1" /> Connect Instagram</Button>
                  <Button data-testid="instagram-test-button" onClick={test} disabled={testing} className="bg-white/10 hover:bg-white/20 text-zinc-100 font-tactical font-bold uppercase text-xs">{testing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />} Test connection</Button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">An Instagram window opens — log in and approve. You'll land back here connected, and your DMs show up under the <strong>DM Inbox</strong> tab and in the Message Command Center.</p>
              </>
            )}
          </Phase>

          <p className="text-[10px] text-zinc-500 pt-1">Stuck on any step? Tell me the number and I'll walk you through it. For your own account you can test right away — no Meta App Review needed. Your codes are stored encrypted.</p>

          {/* Go Live for clients */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden mt-1">
            <button data-testid="ig-golive-toggle" onClick={() => setGoLive((v) => !v)} className="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-white/[0.03]">
              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0 bg-emerald-500/15 text-emerald-300"><Rocket size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-tactical font-bold text-sm text-zinc-100">Go live for clients (optional)</div>
                <div className="text-[11px] text-zinc-500">Needed only to DM people who aren't on your app — submit Meta App Review</div>
              </div>
              <ChevronDown size={16} className={`text-zinc-500 transition-transform ${goLive ? "rotate-180" : ""}`} />
            </button>
            {goLive && (
              <div className="px-3.5 pb-4 pt-1 anim-slidein">
                <Why>For <strong>your own</strong> account (and anyone added as a tester) everything already works. App Review is only needed so LifeOS can message <strong>any</strong> customer in production.</Why>
                <div className="text-[12px] text-zinc-300 mb-1">Request Advanced Access for exactly these two permissions:</div>
                <CopyRow label="Permission 1" value="instagram_business_basic" />
                <CopyRow label="Permission 2" value="instagram_business_manage_messages" />
                <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-tactical font-bold text-emerald-300 border border-emerald-400/30 rounded-lg px-3 py-1.5 my-3 hover:bg-emerald-500/15">Open your app in Meta <ExternalLink size={12} /></a>
                <Steps items={[
                  <>In <strong>App settings → Basic</strong>, add your <strong>Privacy Policy URL</strong> and <strong>Terms URL</strong>, upload an app <strong>icon</strong>, and pick a category. (These are required before review.)</>,
                  <>Complete <strong>Business verification</strong> under <strong>Meta Business settings</strong> (verify your business details — this can take a day or two).</>,
                  <>Go to <strong>App Review → Permissions and Features</strong>. Next to <strong>instagram_business_manage_messages</strong> and <strong>instagram_business_basic</strong>, click <strong>Request Advanced Access</strong>.</>,
                  <>Record a short screen video showing a customer DMing you and you replying inside LifeOS, and write a 2-3 sentence description of how messaging is used. Attach both.</>,
                  <>Submit for review. When approved, flip your app from <strong>Development</strong> to <strong>Live</strong> (toggle at the top of the dashboard).</>,
                ]} />
                <p className="text-[11px] text-zinc-500 mt-2">Want me to draft the reviewer description and the exact test steps for your screencast? Just ask.</p>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
};
